// frontend/src/components/LOS/Caprion/BureauVerification.tsx
//
// Bureau Verification Page
// ========================
//
// Dedicated page for polling bureau check status after lender selection.
// This page is shown AFTER Select Lender page and BEFORE AA Consent page.
//
// Flow:
// 1. User selects lender and create-lead API is called on previous page
// 2. This page automatically starts bureau status polling
// 3. Polls every 5 seconds for up to 10 minutes (120 attempts)
// 4. Handles terminal statuses:
//    - Pending: Continue polling (not terminal, proceed when ready)
//    - Accepted/Approved: Proceed to next page (TERMINAL SUCCESS)
//    - Rejected: Show rejection screen, block navigation (TERMINAL FAILURE)
//    - Withdrawn: Show rejection screen, block navigation (TERMINAL FAILURE)
//
// Uses bureau_context to separate from loan verification polling.

import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '@/config';
import { useCaprionRejectionGuard } from '@/hooks/useCaprionRejectionGuard';
import CaprionRejectionScreen from './CaprionRejectionScreen';
import CaprionStatusLoader from './CaprionStatusLoader';
import { Shield, FileSearch, CheckCircle2, Clock } from 'lucide-react';

interface BureauVerificationProps {
  onDone?: () => void;
  isBuilderMode?: boolean;
  registerSubmit?: (submitFn: () => Promise<boolean>) => void;
}

const BureauVerification: React.FC<BureauVerificationProps> = ({
  onDone,
  isBuilderMode = false,
  registerSubmit
}) => {
  // Check rejection status on page load (1st check - prevents unnecessary polling)
  const { isRejected: isRejectedOnLoad, rejectionReason: rejectionReasonOnLoad, rejectionTimestamp } = useCaprionRejectionGuard();
  
  const [isPolling, setIsPolling] = useState(false);
  const [pollCount, setPollCount] = useState(0);
  const [loanStatus, setLoanStatus] = useState<string | null>(null);
  
  // State for rejection from polling (2nd check - after status poll completes)
  const [pollingRejected, setPollingRejected] = useState(false);
  const [pollingRejectionReason, setPollingRejectionReason] = useState<string | null>(null);
  
  const [error, setError] = useState<string | null>(null);
  
  const pollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const pollCountRef = useRef(0);
  const hasStartedRef = useRef(false); // Flag to prevent duplicate calls
  const MAX_POLL_ATTEMPTS = 120; // 120 * 5 seconds = 10 minutes


  // Start polling on component mount (only once, even in StrictMode)
  // Skip polling if already rejected on page load
  useEffect(() => {
    if (!isBuilderMode && !hasStartedRef.current && !isRejectedOnLoad) {
      hasStartedRef.current = true;
      startPolling();
    }
    
    return () => {
      // Cleanup on unmount
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    };
  }, [isRejectedOnLoad]);

  const startPolling = async () => {
    try {
      setIsPolling(true);
      setError(null);
      pollCountRef.current = 0;

      console.log('[BUREAU VERIFICATION] Starting bureau status poll (bureau_context)...');
      
      // Start bureau status poll with bureau_context
      const startPollResponse = await axios.post(
        `${API_BASE_URL}/api/caprion/start-status-poll/`,
        { poll_context: 'bureau_context' },
        { withCredentials: true }
      );

      if (!startPollResponse.data.success) {
        throw new Error(startPollResponse.data.error || 'Failed to start status poll');
      }

      const taskId = startPollResponse.data.task_id;
      console.log('[BUREAU VERIFICATION] Poll started, task_id:', taskId);

      // Start polling the task status
      const pollStatus = async () => {
        try {
          pollCountRef.current++;
          setPollCount(pollCountRef.current);
          console.log(`[BUREAU VERIFICATION] Poll attempt ${pollCountRef.current}/${MAX_POLL_ATTEMPTS}`);

          const statusResponse = await axios.get(
            `${API_BASE_URL}/api/caprion/poll-status/${taskId}/`,
            { withCredentials: true }
          );

          if (statusResponse.data.success && statusResponse.data.is_ready) {
            const currentLoanStatus = statusResponse.data.result?.loan_status;
            setLoanStatus(currentLoanStatus);
            console.log(`[BUREAU VERIFICATION] Polling complete, loan_status: ${currentLoanStatus}`);

            // Stop polling
            if (pollIntervalRef.current) {
              clearInterval(pollIntervalRef.current);
              pollIntervalRef.current = null;
            }
            setIsPolling(false);

            // Handle each status
            
            // 1. Created - Keep polling (initial status)
            if (currentLoanStatus === 'Created') {
              console.log('[BUREAU VERIFICATION] Status: Created - continuing poll...');
              if (pollCountRef.current >= MAX_POLL_ATTEMPTS) {
                throw new Error('Bureau verification is taking longer than expected. Please try again later.');
              }
            }
            
            // 2. Pending or Ops Verification - Proceed to next page (TERMINAL SUCCESS)
            else if (currentLoanStatus === 'Pending' || currentLoanStatus === 'Ops Verification') {
              console.log(`[BUREAU VERIFICATION] ✅ Status: ${currentLoanStatus} - proceeding...`);
              
              // Proceed to next page after 1 second
              setTimeout(() => {
                if (onDone) {
                  onDone();
                }
              }, 1000);
            }
            
            // 3. Accepted - Proceed to next page (TERMINAL SUCCESS)
            else if (currentLoanStatus === 'Accepted') {
              console.log('[BUREAU VERIFICATION] ✅ Status: Accepted - proceeding...');
              
              // Proceed to next page after 1 second
              setTimeout(() => {
                if (onDone) {
                  onDone();
                }
              }, 1000);
            }
            
            // 4. Approved - Proceed to next page (TERMINAL SUCCESS)
            else if (currentLoanStatus === 'Approved') {
              console.log('[BUREAU VERIFICATION] ✅ Status: Approved - proceeding...');
              
              // Proceed to next page after 1 second
              setTimeout(() => {
                if (onDone) {
                  onDone();
                }
              }, 1000);
            }
            
            // 5. Rejected - Show rejection screen (TERMINAL FAILURE)
            else if (currentLoanStatus === 'Rejected') {
              const backendMessage = statusResponse.data.result?.message || '';
              // Filter out technical messages that shouldn't be shown to users
              const reason = backendMessage.includes('Final status already captured')
                ? 'Credit bureau verification failed - Unable to verify your credit history. The bureau data shows inconsistencies or insufficient credit information.'
                : backendMessage || 'Credit bureau verification failed - Unable to verify your credit history. The bureau data shows inconsistencies or insufficient credit information.';
              console.log('[BUREAU VERIFICATION] ❌ Application Rejected!');
              
              // Set rejection state to show rejection screen
              // Backend Celery task automatically updates DB (is_rejected, rejection_reason, rejection_timestamp)
              setPollingRejectionReason(reason);
              setPollingRejected(true);
            }
            
            // 6. Withdrawn - Show rejection screen (TERMINAL FAILURE)
            else if (currentLoanStatus === 'Withdrawn') {
              const backendMessage = statusResponse.data.result?.message || '';
              // Filter out technical messages that shouldn't be shown to users
              const reason = backendMessage.includes('Final status already captured')
                ? 'Credit bureau verification failed - Your application has been withdrawn by the lender.'
                : backendMessage || 'Credit bureau verification failed - Your application has been withdrawn by the lender.';
              console.log('[BUREAU VERIFICATION] ❌ Application Withdrawn!');
              
              // Set rejection state to show rejection screen
              // Backend Celery task automatically updates DB (is_rejected, rejection_reason, rejection_timestamp)
              setPollingRejectionReason(reason);
              setPollingRejected(true);
            }
            
            // 7. Unknown status - Continue polling
            else {
              console.log(`[BUREAU VERIFICATION] Unknown status: ${currentLoanStatus} - continuing poll...`);
              if (pollCountRef.current >= MAX_POLL_ATTEMPTS) {
                throw new Error('Bureau verification timed out. Please try again later.');
              }
            }
          } else if (pollCountRef.current >= MAX_POLL_ATTEMPTS) {
            // Max attempts reached
            throw new Error('Bureau verification is taking longer than expected. Please try again later.');
          }
        } catch (err: any) {
          console.error('[BUREAU VERIFICATION] Poll error:', err);
          if (pollIntervalRef.current) {
            clearInterval(pollIntervalRef.current);
            pollIntervalRef.current = null;
          }
          setIsPolling(false);
          setError(err.message || 'Failed to verify bureau status');
        }
      };

      // Start polling immediately, then every 5 seconds
      pollStatus();
      pollIntervalRef.current = setInterval(pollStatus, 5000);

    } catch (err: any) {
      console.error('[BUREAU VERIFICATION] Error starting poll:', err);
      setIsPolling(false);
      setError(err.message || 'Failed to start bureau verification');
    }
  };

  // Register submit function (blocks navigation if rejected)
  useEffect(() => {
    if (registerSubmit) {
      // Block navigation if rejected (from page load check OR polling check)
      if (isRejectedOnLoad || pollingRejected) {
        registerSubmit(async () => false);
      } else {
        registerSubmit(async () => true);
      }
    }
  }, [registerSubmit, isRejectedOnLoad, pollingRejected]);

  // Builder mode preview
  if (isBuilderMode) {
    return (
      <div className="rounded-xl border border-dashed border-gray-300 bg-gray-50 p-4 text-center text-sm text-gray-400">
        <div className="text-2xl mb-1">🔄</div>
        <p className="font-semibold text-gray-500">Caprion: Bureau Verification</p>
        <p className="text-xs mt-1">
          Polls bureau check status after lender selection. Handles: Pending/Accepted/Approved (proceed), Rejected/Withdrawn (block).
        </p>
      </div>
    );
  }

  // Show rejection screen if rejected (from page load check OR polling check)
  if (isRejectedOnLoad || pollingRejected) {
    return (
      <CaprionRejectionScreen
        reason={pollingRejected ? pollingRejectionReason || undefined : rejectionReasonOnLoad || undefined}
        timestamp={rejectionTimestamp || new Date().toISOString()}
        context="bureau"
      />
    );
  }

  // Custom steps for bureau verification context
  const bureauVerificationSteps = [
    { icon: Shield, text: 'Verifying your credit profile', duration: 2500 },
    { icon: FileSearch, text: 'Checking bureau records', duration: 3000 },
    { icon: Clock, text: 'Processing with lender', duration: 3500 },
    { icon: CheckCircle2, text: 'Validating application status', duration: 4000 },
  ];

  // Show polling UI with custom bureau verification steps
  return <CaprionStatusLoader title="Bureau Verification" customSteps={bureauVerificationSteps} />;
};

export default BureauVerification;
